import { getApiBaseUrl } from '@/config/environment';

export type MFAMethod = 'sms' | 'totp';

export interface MFALoginResponse {
  success: boolean;
  requires2FA: boolean;
  requires2FASetup?: boolean;
  userId?: string;
  /** Signed, short-lived challenge from the password step; required by every MFA call */
  mfaToken?: string;
  mfaMethod?: MFAMethod;
  phoneHint?: string;
  // Only present when MFA is not required (shouldn't happen with mandatory MFA)
  user?: any;
  accessToken?: string;
  refreshToken?: string;
}

export interface TOTPSetupResponse {
  success: boolean;
  secret: string;
  qrCodeUrl: string;
  manualEntryCode: string;
}

export interface VerifyResponse {
  success: boolean;
  user: any;
  accessToken: string;
  refreshToken: string;
  // Only on TOTP setup
  backupCodes?: string[];
  verified?: boolean;
}

export const MFA_CHALLENGE_KEY = 'adminMfaToken';

class MFAAPIService {
  // Every MFA call proves the password step passed with the challenge issued at login
  private withChallenge(data: Record<string, unknown>): string {
    return JSON.stringify({ ...data, mfaToken: sessionStorage.getItem(MFA_CHALLENGE_KEY) || undefined });
  }

  // Generate TOTP secret + QR code for setup
  async generateTOTP(userId: string): Promise<TOTPSetupResponse> {
    const response = await fetch(`${getApiBaseUrl()}/api/v1/auth/admin/2fa/generate`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: this.withChallenge({ userId }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error?.message || data.message || 'Failed to generate 2FA secret');
    }

    return response.json();
  }

  // Verify TOTP setup and enable 2FA (returns tokens + backup codes)
  async verifyTOTPSetup(userId: string, secret: string, token: string): Promise<VerifyResponse> {
    const response = await fetch(`${getApiBaseUrl()}/api/v1/auth/admin/2fa/verify-setup`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: this.withChallenge({ userId, secret, token }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error?.message || data.message || 'Invalid verification code');
    }

    return response.json();
  }

  // Verify TOTP code during login
  async verifyTOTP(userId: string, token: string): Promise<VerifyResponse> {
    const response = await fetch(`${getApiBaseUrl()}/api/v1/auth/admin/verify-2fa`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: this.withChallenge({ userId, token }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error?.message || data.message || 'Invalid verification code');
    }

    return response.json();
  }

  // Verify SMS code during login
  async verifySMS(userId: string, token: string): Promise<VerifyResponse> {
    const response = await fetch(`${getApiBaseUrl()}/api/v1/auth/admin/verify-sms`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: this.withChallenge({ userId, token }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error?.message || data.message || 'Invalid verification code');
    }

    return response.json();
  }

  // Check if admin has a phone number on file (added by super admin)
  async checkPhoneOnFile(userId: string): Promise<{ hasPhone: boolean; phoneHint?: string }> {
    const response = await fetch(`${getApiBaseUrl()}/api/v1/auth/admin/mfa/check-phone`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: this.withChallenge({ userId }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error?.message || data.message || 'Failed to check phone availability');
    }

    return response.json();
  }

  // Setup SMS MFA — send code to phone on file
  async setupSMS(userId: string): Promise<{ success: boolean; phoneHint: string }> {
    const response = await fetch(`${getApiBaseUrl()}/api/v1/auth/admin/mfa/setup-sms`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: this.withChallenge({ userId }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error?.message || data.message || 'Failed to setup SMS MFA');
    }

    return response.json();
  }

  // Verify SMS MFA setup and enable MFA (returns tokens)
  async verifySMSSetup(userId: string, code: string): Promise<VerifyResponse> {
    const response = await fetch(`${getApiBaseUrl()}/api/v1/auth/admin/mfa/verify-setup-sms`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: this.withChallenge({ userId, code }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error?.message || data.message || 'Invalid verification code');
    }

    return response.json();
  }

  // Resend SMS code
  async resendSMS(userId: string): Promise<{ success: boolean; phoneHint: string }> {
    const response = await fetch(`${getApiBaseUrl()}/api/v1/auth/admin/resend-sms`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: this.withChallenge({ userId }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error?.message || data.message || 'Failed to resend code');
    }

    return response.json();
  }
}

export const mfaAPI = new MFAAPIService();
